import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Search, Crown } from "lucide-react";
import SectionHeader from "./SectionHeader";
import AfricaMap from "./AfricaMap";
import CountryDialog, { NationFlag } from "./CountryDialog";
import { NATIONS, REGIONS, VISA_GREENS, VOA_RED, DIASPORA } from "../data/nations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Nations() {
  const [query, setQuery] = useState("");
  const [region, setRegion] = useState("All");
  const [selected, setSelected] = useState(null);
  const [nations, setNations] = useState(NATIONS);

  useEffect(() => {
    axios
      .get(`${API}/nations/visa-status`)
      .then((r) => {
        const ov = r.data.overrides || {};
        setNations(NATIONS.map((n) => (ov[n.code] !== undefined ? { ...n, visaFree: ov[n.code] } : n)));
      })
      .catch(() => {});
  }, []);

  const filtered = useMemo(
    () =>
      nations.filter(
        (n) =>
          (region === "All" || n.region === region || n.founding) &&
          n.name.toLowerCase().includes(query.trim().toLowerCase())
      ),
    [query, region, nations]
  );

  const diasporaVisible =
    (region === "All" || region === "Diaspora") &&
    DIASPORA.name.toLowerCase().includes(query.trim().toLowerCase());

  const display = useMemo(() => {
    const list = [...filtered];
    if (diasporaVisible) list.splice(list.length > 0 ? 1 : 0, 0, DIASPORA);
    return list;
  }, [filtered, diasporaVisible]);

  const visaFreeCount = nations.filter((n) => n.visaFree).length;

  return (
    <section id="nations" className="py-20 lg:py-32 bg-[#0D1013]" data-testid="nations-section">
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <SectionHeader
            kicker="The Union Matrix"
            title="55 Nations & The Sixth Region"
            copy="The founding Kingdom of Kush stands first, followed by fifty-four sovereign states — and the African Diaspora, the Sixth Region of the Union, 350 million people of African descent worldwide. Nations in green are visa-free for every holder of the One Passport — tap any card to open its file."
          />
          <div className="shrink-0 lg:text-right">
            <p className="font-display font-extrabold text-4xl text-[#00A651]" data-testid="nations-count">
              {filtered.length}
              <span className="text-[#8A99AD] text-xl"> / 55</span>
            </p>
            <p className="font-official text-[10px] tracking-[0.2em] uppercase text-[#4ADE80] mt-2" data-testid="visa-free-count">
              {visaFreeCount} Visa-Free Nations
            </p>
          </div>
        </div>

        <div className="mt-12">
          <AfricaMap nations={nations} onSelect={setSelected} />
        </div>

        <div className="mt-12 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div className="relative w-full md:max-w-sm">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A99AD]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search a member nation..."
              data-testid="country-search-input"
              className="w-full rounded-full bg-[#121519] border border-[#00A651]/20 pl-11 pr-4 py-3 text-sm text-[#F7F5F0] placeholder:text-[#8A99AD]/60 focus:outline-none focus:border-[#00A651]/60 transition-colors"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {REGIONS.map((r) => (
              <button
                key={r}
                onClick={() => setRegion(r)}
                data-testid={`region-filter-${r.toLowerCase()}`}
                className={`rounded-full px-4 py-2 font-official text-[11px] tracking-[0.15em] uppercase transition-all duration-300 ${
                  region === r
                    ? "bg-[#00A651] text-[#0A0C0E]"
                    : "border border-[#00A651]/20 text-[#8A99AD] hover:border-[#00A651]/50 hover:text-[#00A651]"
                }`}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2 font-official text-[10px] tracking-[0.2em] uppercase" data-testid="visa-legend">
          <span className="flex items-center gap-2 text-[#4ADE80]">
            <span className="w-2.5 h-2.5 rounded-full bg-[#4ADE80]" />
            Visa-Free — One Passport
          </span>
          <span className="flex items-center gap-2" style={{ color: VOA_RED }}>
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: VOA_RED }} />
            Visa on Arrival / E-Visa
          </span>
          <span className="flex items-center gap-2 text-[#00A651]">
            <span className="w-2.5 h-2.5 rounded-full bg-[#00A651]" />
            Founding State — Kingdom of Kush
          </span>
          <span className="flex items-center gap-2 text-[#4ADE80]">
            <span className="w-2.5 h-2.5 rounded-full bg-[#4ADE80]" />
            Sixth Region — African Diaspora
          </span>
        </div>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="nations-grid">
          {display.map((n, i) => (
            <motion.button
              key={n.code}
              type="button"
              onClick={() => setSelected(n)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: (i % 5) * 0.06 }}
              className={`group text-left rounded-xl p-5 hover:-translate-y-1 transition-all duration-300 cursor-pointer ${
                n.founding
                  ? "border border-[#00A651]/60 bg-gradient-to-br from-[#1A1F26] to-[#121519] shadow-[0_0_32px_rgba(0,166,81,0.12)] hover:border-[#00A651]"
                  : n.diaspora
                  ? "border border-[#4ADE80]/45 bg-gradient-to-br from-[#12211A] to-[#121519] shadow-[0_0_32px_rgba(74,222,128,0.10)] hover:border-[#4ADE80]"
                  : "border border-[#00A651]/12 bg-[#121519] hover:border-[#00A651]/45 hover:bg-[#1A1F26]"
              }`}
              data-testid={`country-card-${n.code.toLowerCase()}`}
            >
              <div className="flex items-start justify-between">
                <NationFlag nation={n} className="w-10 h-7" />
                {n.founding && <Crown className="w-4 h-4 text-[#00A651]" />}
              </div>
              <h3
                className="font-display font-bold text-sm mt-4 leading-snug"
                style={{ color: n.visaFree ? VISA_GREENS[i % VISA_GREENS.length] : VOA_RED }}
              >
                {n.name}
              </h3>
              <p className="font-official text-[10px] tracking-[0.2em] text-[#8A99AD] mt-2 uppercase">
                {n.capital} • {n.bloc}
              </p>
              <p
                className={`font-official text-[10px] tracking-[0.2em] mt-2 uppercase ${n.visaFree ? "text-[#4ADE80]" : ""}`}
                style={n.visaFree ? undefined : { color: VOA_RED }}
                data-testid={`visa-status-${n.code.toLowerCase()}`}
              >
                {n.visaFree ? (n.diaspora ? "Visa-Free — Welcome Home" : "Visa-Free") : "Visa on Arrival"}
              </p>
              <div className="mt-4 h-1 rounded-full overflow-hidden flex opacity-70">
                <span className="flex-1 bg-[#009B4D]" />
                <span className="flex-1 bg-[#FFD700]" />
                <span className="flex-1 bg-[#CE1126]" />
              </div>
            </motion.button>
          ))}
        </div>
        {filtered.length === 0 && !diasporaVisible && (
          <p className="mt-10 font-official text-xs tracking-[0.2em] uppercase text-[#8A99AD]" data-testid="nations-empty">
            No nation matches — the Union grows daily.
          </p>
        )}
      </div>

      <CountryDialog nation={selected} onClose={() => setSelected(null)} />
    </section>
  );
}
